import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CornerDownRight, Info } from 'lucide-react';
import type { DirtyForm } from '../../hooks/useDirtyForm';
import ConfirmModal from '../shared/ConfirmModal';
import { ChangedDot, SheetRow, SheetSection } from '../settings/sheet';
import { SettingsSwitch } from '../settings/storeSettingsForm';
import { PrintingPrinter, RoutingRule } from './printingApi';
import {
  CategoryRow, Effective, OrderTargetsInfo, PageForm, effectiveTarget, inheritedTarget, ruleCount, targetIds, ticketPrinters,
} from './printingModel';
import { Notice, SELECT_CLS } from './printingUi';

/**
 * «Δελτία ανά κατηγορία προϊόντων» (απόφαση ιδιοκτήτη 25/10/2026): κανονικός διακόπτης. Κλειστός = κάθε
 * δελτίο βγαίνει ολόκληρο στον κύριο εκτυπωτή δελτίων (ή στον εκτυπωτή του Take Away/των τραπεζιών)·
 * ανοιχτός = ο πίνακας (υποκατηγορία «όπως η κατηγορία» εκτός αν οριστεί). Το κλείσιμο με κανόνες ρωτά
 * πρώτα: οι κανόνες σβήνονται με την «Αποθήκευση» της σελίδας, όχι αμέσως.
 */

interface Props {
  rows: CategoryRow[];
  printers: PrintingPrinter[];
  form: DirtyForm<PageForm>;
  /** Κανόνες για κατηγορίες εκτός λίστας — μετρούν κι αυτοί στο «θα σβηστούν». */
  passthrough: RoutingRule[];
  takeaway: boolean;
  dineIn: boolean;
  orderTargets: OrderTargetsInfo;
}

interface Option { value: string; label: string }

const CategoryRoutingSection: React.FC<Props> = ({ rows, printers, form, passthrough, takeaway, dineIn, orderTargets }) => {
  const { t } = useTranslation(['printing']);
  const { values: v, saved, setField, changedKeys } = form;
  const [confirmOff, setConfirmOff] = useState(false);
  const tickets = ticketPrinters(printers);
  const byId = useMemo(() => new Map(rows.map(r => [r.id, r])), [rows]);
  const unrouted = v.unrouted === '' ? null : Number(v.unrouted);
  const ticketIds = new Set(tickets.map(p => p.id));

  const name = (id: number) => printers.find(p => p.id === id)?.name || `#${id}`;
  const names = (ids: number[]) => ids.map(name).join(' + ');
  const optionsWith = (...current: string[]): Option[] => {
    const out: Option[] = tickets.map(p => ({ value: String(p.id), label: p.name }));
    current.forEach(value => {
      if (!value || out.some(o => o.value === value)) return;
      const ids = targetIds(value);
      const label = names(ids);
      out.push({ value, label: ids.every(id => ticketIds.has(id)) ? label : t('printing:routing.notTickets', { name: label }) });
    });
    return out;
  };
  const targetText = (eff: Effective) => (eff.ids.length > 0 ? names(eff.ids) : t('printing:routing.targetNone'));
  // Η κρυφή «__uncategorized__» του Καταλόγου: τα είδη χωρίς κατηγορία.
  const rowName = (r: CategoryRow) => (r.system ? t('printing:routing.uncategorized') : r.name);
  const rowChanged = (id: number) => (v.rules[String(id)] || '') !== (saved.rules[String(id)] || '');
  const setRule = (id: number, value: string) => form.setValues(prev => ({ ...prev, rules: { ...prev.rules, [String(id)]: value } }));

  const silent = rows.filter(r => effectiveTarget(r, byId, v.rules, unrouted).source === 'none');
  const count = ruleCount(v, passthrough);
  const dirty = changedKeys.includes('rules') || changedKeys.includes('byCategory');

  // Take Away/Τραπέζια με δικό τους εκτυπωτή δεν περνούν από τον πίνακα (ένα δελτίο με όλη την
  // παραγγελία): η σημείωση πάνω από τον πίνακα λέει για ποιες παραγγελίες ισχύει ακόμη.
  const ownTakeaway = orderTargets.supported && takeaway && v.takeaway !== '';
  const ownDinein = orderTargets.supported && dineIn && v.dinein !== '';
  let tableNote: string | null = null;
  if (ownTakeaway && ownDinein) {
    tableNote = t('printing:routing.tableNoteBoth');
  } else if (ownTakeaway) {
    tableNote = [t('printing:routing.tableNoteTakeaway', { name: names(targetIds(v.takeaway)) }),
      dineIn ? t('printing:routing.tableStillDinein') : ''].join(' ').trim();
  } else if (ownDinein) {
    tableNote = [t('printing:routing.tableNoteDinein', { name: names(targetIds(v.dinein)) }),
      takeaway ? t('printing:routing.tableStillTakeaway') : ''].join(' ').trim();
  }

  const toggle = (next: boolean) => {
    if (!next && count > 0) setConfirmOff(true);
    else setField('byCategory', next);
  };
  const mainName = unrouted != null ? name(unrouted) : null;

  return (
    <SheetSection id="printing-categories" title={t('printing:categories.title')} hint={t('printing:categories.hint')}
      dirty={dirty} testId="categories-section">
      <SheetRow
        title={t('printing:categories.switch')}
        subtitle={t('printing:categories.switchSub')}
        note={!v.byCategory ? (mainName
          ? t('printing:categories.offNote', { name: mainName })
          : t('printing:categories.offNoteNone')) : undefined}
        changed={changedKeys.includes('byCategory')}
        control={({ labelId, descriptionId }) => (
          <SettingsSwitch checked={v.byCategory} onChange={toggle} labelId={labelId} describedById={descriptionId} />
        )}
      />

      {!v.byCategory && saved.byCategory && (
        <div className="mt-3"><Notice testId="rules-will-clear">{t('printing:categories.willClear')}</Notice></div>
      )}

      {v.byCategory && (
        <div className="mt-4" data-testid="category-table">
          {tickets.length > 0 && silent.length > 0 && (
            <div className="mb-3"><Notice testId="warn-silent">{t('printing:routing.warnUnroutedCount', { count: silent.length })}</Notice></div>
          )}
          {rows.length === 0 ? (
            <p className="text-sm text-t3">{t('printing:routing.noCategories')}</p>
          ) : (
            <>
              {tableNote && (
                <p className="mb-3 flex items-start gap-2 text-[13px] leading-[19px] text-t2" data-testid="routing-table-note">
                  <Info className="w-4 h-4 mt-0.5 shrink-0 text-t3" aria-hidden="true" />
                  <span className="min-w-0">{tableNote}</span>
                </p>
              )}
              <table className="w-full table-fixed text-sm" aria-label={t('printing:routing.tableAria')}>
                <thead>
                  <tr className="text-left text-xs font-semibold text-t3">
                    <th scope="col" className="pb-2 pr-3 font-semibold">{t('printing:routing.colCategory')}</th>
                    <th scope="col" className="pb-2 w-[52%] font-semibold">{t('printing:routing.colPrinter')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line border-t border-line">
                  {rows.map(r => {
                    const value = v.rules[String(r.id)] || '';
                    const inherited = inheritedTarget(r, byId, v.rules, unrouted);
                    const sub = r.parentId !== null;
                    const defaultLabel = sub
                      ? t('printing:routing.inheritParent', { target: targetText(inherited) })
                      : t('printing:routing.inheritUnrouted', { target: targetText(inherited) });
                    const none = value === '' && inherited.source === 'none';
                    return (
                      <tr key={r.id} data-testid={`route-row-${r.id}`} data-source={value ? 'own' : inherited.source}>
                        <td className="py-2 pr-3 align-middle">
                          <span className="flex items-center gap-1.5 min-w-0" style={{ paddingLeft: `${Math.min(r.depth, 4) * 18}px` }}>
                            {sub && <CornerDownRight className="w-3.5 h-3.5 shrink-0 text-t4" aria-hidden="true" />}
                            <span className={`min-w-0 break-words ${r.hidden && !r.system ? 'text-t3' : 'text-t1'} ${sub ? '' : 'font-medium'}`}>{rowName(r)}</span>
                            {r.hidden && !r.system && <span className="shrink-0 px-1.5 rounded text-[11px] font-medium bg-surface-3 text-t3">{t('printing:routing.hidden')}</span>}
                            {rowChanged(r.id) && <ChangedDot />}
                            {none && <AlertTriangle role="img" className="w-3.5 h-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-label={t('printing:routing.rowSilent')} />}
                          </span>
                        </td>
                        <td className="py-2 align-middle">
                          <select
                            value={value}
                            onChange={e => setRule(r.id, e.target.value)}
                            aria-label={t('printing:routing.rowAria', { name: rowName(r) })}
                            className={`${SELECT_CLS} min-h-[40px] py-2`}
                            // Χωρίς δικό της κανόνα = αχνό· inline, γιατί το ανοιχτό θέμα βάζει text-1 σε κάθε select.
                            style={value === '' ? { color: 'hsl(var(--text-3))' } : undefined}
                          >
                            <option value="">{defaultLabel}</option>
                            {optionsWith(value, saved.rules[String(r.id)] || '').map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={confirmOff}
        title={t('printing:categories.confirmTitle')}
        message={mainName
          ? t('printing:categories.confirmMessage', { count, name: mainName })
          : t('printing:categories.confirmMessageNone', { count })}
        confirmLabel={t('printing:categories.confirmOff')}
        cancelLabel={t('printing:cancel')}
        variant="warning"
        onConfirm={() => { setConfirmOff(false); setField('byCategory', false); }}
        onCancel={() => setConfirmOff(false)}
      />
    </SheetSection>
  );
};

export default CategoryRoutingSection;
